import type { AddPanelOptions, DockviewApi, IDockviewPanelProps, Parameters as DockviewParameters, SerializedDockview } from "dockview"
import type { SvgIconProps } from "@mui/material/SvgIcon"
import { type JSX, type Ref } from "react"
import type React from "react"

export type DimensionValue = number | `${number}%`

export interface PanelDimensionRange {
  min?: DimensionValue
  max?: DimensionValue
}

export interface PanelDimensionLimitOptions {
  /** Preferred initial width of the panel in pixels. */
  initialWidth?: number
  /** Preferred initial height of the panel in pixels. */
  initialHeight?: number
  /** Width limits for the panel using either pixel or percentage constraints. */
  widthRange?: PanelDimensionRange
  /** Height limits for the panel using either pixel or percentage constraints. */
  heightRange?: PanelDimensionRange
}

export type OPDockviewPanelDefinition<P extends DockviewParameters = DockviewParameters> = AddPanelOptions<P> & PanelDimensionLimitOptions

export type TabIconComponent = React.ComponentType<SvgIconProps>

export interface OPLayoutPanelTabOptions {
  icon?: TabIconComponent
  removable?: boolean
  removePanel?: (panelId: string, requiresConfirmation?: boolean) => void
  removeRequiresConfirmation?: boolean
  editable?: boolean
  onUpdateTitle?: (panelId: string, newTitle: string) => void
  duplicatePanel?: () => void
}

export type OPLayoutPanelTabMetadata = Omit<OPLayoutPanelTabOptions, "removable" | "editable"> & {
  removable: boolean
  editable: boolean
}

export interface OPDockviewComponentConfig<P extends DockviewParameters = DockviewParameters> {
  panel: OPDockviewPanelDefinition<P>
  renderer: (props: IDockviewPanelProps<P>) => JSX.Element
}

export interface OPLayoutComponent extends OPLayoutPanelTabOptions {
  component: string
  title: string
  renderer: (props: IDockviewPanelProps) => JSX.Element
  priority?: number
  /** When true, incremental adds use a floating group (see applyPanelChanges). */
  floating?: boolean
}

export interface OPLayoutPanel {
  component: OPLayoutComponent
  panelId: string
  instanceIndex: number
}

export interface OPLayoutTemplateDefaults {
  renderer?: OPDockviewPanelDefinition["renderer"]
  inactive?: boolean
}

export interface OPLayoutTemplate {
  id: string
  defaults: OPLayoutTemplateDefaults
  maxPanels?: number
  compile(panels: readonly OPLayoutPanel[]): OPDockviewPanelDefinition[]
}

export interface OpLayoutEngineRendererActions {
  /** Clears any persisted layout and reapplies the provided default panel definitions. */
  resetLayout(api: DockviewApi): void
  /** Returns the underlying Dockview API instance if available. */
  getApi(): DockviewApi | null
  /** Returns panel IDs in layout order (left-to-right, top-to-bottom). */
  getPanelOrder(): string[]
}

export interface OPDockviewPersistence {
  load(): SerializedDockview | undefined | null
  save(state: SerializedDockview): void
  clear(): void
  /** Returns true when the persistence layer is still fetching existing state. */
  loading?: boolean
}

export interface OpLayoutEngineRendererProps {
  components: readonly OPLayoutComponent[]
  layoutRef: Ref<OpLayoutEngineRendererActions | null>
  onLayoutReady?: (actions: OpLayoutEngineRendererActions | null) => void
  template?: OPLayoutTemplate
  persistence?: OPDockviewPersistence
  className?: string
  removePanel?: (panelId: string) => void
}

export const DOCKVIEW_LOCATION_TYPE_FLOATING = "floating" as const
